import client from '@/services/http/client'
import { isMockEnabled, mockDelay, paginate, sortByParam } from '@/services/http/mock'
import type { PaginatedData } from '@/types/api'
import type { ManualRoomOption, MessageItem, MessageDetail, MessageMedia } from '@/types/message'

// ============== Mock 数据 ==============
function makeMockOperatorActions(status: string): string[] {
  return getMockOperatorActions(status)
}

const mockMessages: MessageItem[] = [
  {
    message_id: 'msg-001',
    room_id: 'room-hospital-support',
    room_name: '某医院售后群',
    customer_name: '某医院',
    sender_name: '张三',
    content_preview: '报到机查询排班提示未在班，无法正常使用，请协助排查。',
    content_type: 'text',
    binding_status: 'matched',
    message_status: 'pending_response',
    send_time: '2026-05-14T10:10:00+08:00',
    ingested_at: '2026-05-14T10:10:30+08:00',
    updated_at: '2026-05-14T10:10:00+08:00',
    operator_actions: makeMockOperatorActions('pending_response'),
  },
  {
    message_id: 'msg-002',
    room_id: 'room-project-tech',
    room_name: '某项目技术群',
    customer_name: '某客户',
    sender_name: '李四',
    content_preview: '系统登录失败，输入正确密码后提示认证异常，已尝试多次。',
    content_type: 'text',
    binding_status: 'matched',
    message_status: 'responded',
    send_time: '2026-05-14T10:18:00+08:00',
    ingested_at: '2026-05-14T10:18:20+08:00',
    updated_at: '2026-05-14T10:25:00+08:00',
    operator_actions: makeMockOperatorActions('responded'),
  },
  {
    message_id: 'msg-003',
    room_id: 'room-after-sale',
    room_name: '售后沟通群',
    customer_name: '',
    sender_name: '王五',
    content_preview: '现场无法叫号，系统一直转圈不响应，急需处理。',
    content_type: 'text',
    binding_status: 'unmatched',
    message_status: 'responded',
    send_time: '2026-05-14T10:22:00+08:00',
    ingested_at: '2026-05-14T10:22:12+08:00',
    updated_at: '2026-05-14T10:30:00+08:00',
    operator_actions: makeMockOperatorActions('responded'),
  },
  {
    message_id: 'msg-004',
    room_id: 'room-hospital-support',
    room_name: '某医院售后群',
    customer_name: '某医院',
    sender_name: '赵六',
    content_preview: '打印机报错，错误代码 E-500，请协助查看。',
    content_type: 'text',
    binding_status: 'matched',
    message_status: 'drafted',
    send_time: '2026-05-14T09:50:00+08:00',
    ingested_at: '2026-05-14T09:50:18+08:00',
    updated_at: '2026-05-14T10:00:00+08:00',
    operator_actions: makeMockOperatorActions('drafted'),
  },
  {
    message_id: 'msg-005',
    room_id: 'room-project-tech',
    room_name: '某项目技术群',
    customer_name: '某客户',
    sender_name: '钱七',
    content_preview: '[图片] 查看截图报错详情',
    content_type: 'image',
    binding_status: 'matched',
    message_status: 'closed_no_ticket',
    send_time: '2026-05-14T09:30:00+08:00',
    ingested_at: '2026-05-14T09:30:22+08:00',
    updated_at: '2026-05-14T09:35:00+08:00',
    operator_actions: makeMockOperatorActions('closed_no_ticket'),
  },
  {
    message_id: 'msg-006',
    room_id: 'room-after-sale',
    room_name: '售后沟通群',
    customer_name: '',
    sender_name: '周八',
    content_preview: '系统运行缓慢，查询数据需要很长时间才能返回结果。',
    content_type: 'text',
    binding_status: 'unmatched',
    message_status: 'binding_required',
    send_time: '2026-05-14T09:00:00+08:00',
    ingested_at: '2026-05-14T09:00:09+08:00',
    updated_at: '2026-05-14T09:05:00+08:00',
    operator_actions: makeMockOperatorActions('binding_required'),
  },
]

const mockMessageContent = new Map<string, string>(
  mockMessages.map((message) => [message.message_id, message.content_preview]),
)

function getMockOperatorActions(messageStatus: string): string[] {
  if (messageStatus === 'pending_response') {
    return ['generate_draft', 'mark_responded', 'close_no_ticket']
  }
  if (messageStatus === 'responded') {
    return ['generate_draft', 'close_no_ticket']
  }
  // drafted / closed_no_ticket / binding_required
  return []
}

function buildMockMedia(message: MessageItem): MessageMedia[] {
  if (!['image', 'file', 'voice', 'video'].includes(message.content_type)) return []
  return [{
    media_uid: `mock-media-${message.message_id}`,
    media_type: message.content_type,
    display_name: message.content_type === 'file' ? 'diagnosis.pdf' : 'screenshot.png',
    file_ext: message.content_type === 'file' ? 'pdf' : 'png',
    mime_type: message.content_type === 'file' ? 'application/pdf' : 'image/png',
    size_bytes: 204800,
    md5sum: null,
    download_status: 'metadata_only',
    preview_status: 'not_supported',
    extract_status: 'not_started',
    extracted_text: null,
    content_available: false,
    download_url: null,
    metadata: {},
  }]
}

function buildMockMessageDetail(message: MessageItem): MessageDetail {
  const rawContent = mockMessageContent.get(message.message_id) ?? message.content_preview

  return {
    message_id: message.message_id,
    raw_content: rawContent,
    normalized_content: rawContent,
    attachments: message.content_type === 'image' ? [{ name: '截图.png', url: '', type: 'image' }] : [],
    media: buildMockMedia(message),
    binding: message.binding_status === 'matched'
      ? { room_name: message.room_name, customer_name: message.customer_name, project_name: message.customer_name || '未命名项目' }
      : null,
    message_events: [
      { event_type: 'received', note: '消息已入库', created_at: message.ingested_at },
    ],
    linked_drafts: message.message_status === 'drafted' ? [{ id: 'draft-001', draft_no: 'DF-20260514-001', status: 'drafted' }] : [],
    operator_actions: getMockOperatorActions(message.message_status),
    message_status: message.message_status,
    room_name: message.room_name,
    customer_name: message.customer_name,
    sender_name: message.sender_name,
    send_time: message.send_time,
    ingested_at: message.ingested_at,
    updated_at: message.updated_at,
  }
}

function normalizeMessageItem(raw: unknown): MessageItem {
  if (!raw || typeof raw !== 'object') {
    throw new Error('Invalid message item response')
  }

  const r = raw as Record<string, unknown>
  return {
    message_id: String(r.message_id ?? ''),
    room_id: String(r.room_id ?? ''),
    room_name: String(r.room_name ?? r.room_id ?? ''),
    customer_name: String(r.customer_name ?? ''),
    sender_name: String(r.sender_name ?? ''),
    content_preview: String(r.content_preview ?? r.content ?? ''),
    content_type: String(r.content_type ?? 'text'),
    binding_status: String(r.binding_status ?? ''),
    message_status: String(r.message_status ?? r.status ?? ''),
    send_time: String(r.send_time ?? ''),
    ingested_at: String(r.ingested_at ?? r.created_at ?? ''),
    updated_at: String(r.updated_at ?? r.ingested_at ?? r.send_time ?? ''),
    operator_actions: Array.isArray(r.operator_actions) ? r.operator_actions.map((item) => String(item)) : [],
  }
}

export function normalizeMessageMedia(raw: unknown): MessageMedia {
  if (!raw || typeof raw !== 'object') {
    throw new Error('Invalid message media response')
  }

  const r = raw as Record<string, unknown>
  const size = r.size_bytes
  return {
    media_uid: String(r.media_uid ?? ''),
    media_type: String(r.media_type ?? 'unknown'),
    display_name: String(r.display_name ?? ''),
    file_ext: r.file_ext == null ? null : String(r.file_ext),
    mime_type: r.mime_type == null ? null : String(r.mime_type),
    size_bytes: typeof size === 'number' ? size : size == null ? null : Number(size),
    md5sum: r.md5sum == null ? null : String(r.md5sum),
    download_status: String(r.download_status ?? 'metadata_only'),
    preview_status: String(r.preview_status ?? 'not_supported'),
    extract_status: String(r.extract_status ?? 'not_started'),
    extracted_text: r.extracted_text == null ? null : String(r.extracted_text),
    content_available: Boolean(r.content_available ?? r.download_url),
    download_url: r.download_url == null ? null : String(r.download_url),
    metadata: r.metadata && typeof r.metadata === 'object' ? (r.metadata as Record<string, unknown>) : {},
  }
}

function normalizeMessageDetail(raw: unknown): MessageDetail {
  if (!raw || typeof raw !== 'object') {
    throw new Error('Invalid message detail response')
  }

  const r = raw as Record<string, unknown>
  const binding =
    r.binding && typeof r.binding === 'object'
      ? {
          room_name: String((r.binding as Record<string, unknown>).room_name ?? ''),
          customer_name: String((r.binding as Record<string, unknown>).customer_name ?? ''),
          project_name: String((r.binding as Record<string, unknown>).project_name ?? ''),
        }
      : null

  return {
    message_id: String(r.message_id ?? ''),
    raw_content: String(r.raw_content ?? r.content ?? ''),
    normalized_content: String(r.normalized_content ?? r.raw_content ?? r.content ?? ''),
    attachments: Array.isArray(r.attachments)
      ? (r.attachments as Array<Record<string, unknown>>).map((item) => ({
          name: String(item.name ?? ''),
          url: String(item.url ?? item.source ?? ''),
          type: String(item.type ?? item.source ?? 'unknown'),
        }))
      : [],
    media: Array.isArray(r.media) ? r.media.map((item) => normalizeMessageMedia(item)) : [],
    binding,
    message_events: Array.isArray(r.message_events)
      ? (r.message_events as Array<Record<string, unknown>>).map((item) => ({
          event_type: String(item.event_type ?? item.status ?? ''),
          note: String(item.note ?? ''),
          created_at: String(item.created_at ?? ''),
        }))
      : [],
    linked_drafts: Array.isArray(r.linked_drafts)
      ? (r.linked_drafts as Array<Record<string, unknown>>).map((item) => ({
          id: String(item.id ?? item.draft_id ?? ''),
          draft_no: String(item.draft_no ?? ''),
          status: String(item.status ?? ''),
        }))
      : [],
    operator_actions: Array.isArray(r.operator_actions) ? r.operator_actions.map((item) => String(item)) : [],
    message_status: String(r.message_status ?? r.status ?? ''),
    room_name: String(r.room_name ?? binding?.room_name ?? ''),
    customer_name: String(r.customer_name ?? binding?.customer_name ?? ''),
    sender_name: String(r.sender_name ?? ''),
    send_time: String(r.send_time ?? ''),
    ingested_at: String(r.ingested_at ?? r.created_at ?? ''),
    updated_at: String(r.updated_at ?? r.ingested_at ?? r.send_time ?? ''),
  }
}

// ============== API 方法 ==============
export async function fetchMessages(params: {
  page?: number
  page_size?: number
  keyword?: string
  message_status?: string
  binding_status?: string
  sort_by?: string
  sort_order?: string
}): Promise<PaginatedData<MessageItem>> {
  if (isMockEnabled()) {
    await mockDelay()
    let filtered = [...mockMessages]
    if (params.keyword) {
      const kw = params.keyword.toLowerCase()
      filtered = filtered.filter(
        (m) =>
          m.room_name.toLowerCase().includes(kw) ||
          m.customer_name.toLowerCase().includes(kw) ||
          m.content_preview.toLowerCase().includes(kw)
      )
    }
    if (params.message_status) {
      filtered = filtered.filter((m) => m.message_status === params.message_status)
    }
    if (params.binding_status) {
      filtered = filtered.filter((m) => m.binding_status === params.binding_status)
    }
    filtered = sortByParam(filtered, params.sort_by, params.sort_order, {
      created_at: 'ingested_at',
      ingested_at: 'ingested_at',
      send_time: 'send_time',
      updated_at: 'updated_at',
    })
    return paginate(filtered, params.page || 1, params.page_size || 20)
  }
  const { data } = await client.get<PaginatedData<Record<string, unknown>>>('/messages', { params })
  return {
    ...data,
    items: data.items.map((item) => normalizeMessageItem(item)),
  }
}

export async function fetchMessageDetail(messageId: string): Promise<MessageDetail> {
  if (isMockEnabled()) {
    await mockDelay()
    const detail = mockMessages.find((message) => message.message_id === messageId)
    if (!detail) throw Object.assign(new Error('消息不存在'), { code: 'NOT_FOUND' })
    return buildMockMessageDetail(detail)
  }
  const { data } = await client.get(`/messages/${messageId}`)
  return normalizeMessageDetail(data)
}

export async function markMessageResponded(messageId: string, note?: string): Promise<void> {
  if (isMockEnabled()) {
    await mockDelay()
    const msg = mockMessages.find((m) => m.message_id === messageId)
    if (msg) msg.message_status = 'responded'
    return
  }
  await client.post(`/messages/${messageId}/mark-responded`, { note })
}

export async function closeMessageNoTicket(messageId: string, reason: string): Promise<void> {
  if (isMockEnabled()) {
    await mockDelay()
    const msg = mockMessages.find((m) => m.message_id === messageId)
    if (msg) msg.message_status = 'closed_no_ticket'
    return
  }
  await client.post(`/messages/${messageId}/close-no-ticket`, { reason })
}

export async function queueMessageMediaDownload(messageId: string, mediaUid: string): Promise<MessageMedia> {
  if (isMockEnabled()) {
    await mockDelay()
    return {
      media_uid: mediaUid,
      media_type: 'image',
      display_name: 'screenshot.png',
      file_ext: 'png',
      mime_type: 'image/png',
      size_bytes: 204800,
      md5sum: null,
      download_status: 'pending',
      preview_status: 'not_supported',
      extract_status: 'not_started',
      extracted_text: null,
      content_available: false,
      download_url: null,
      metadata: {},
    }
  }
  const { data } = await client.post(`/messages/${messageId}/media/${mediaUid}/queue-download`)
  return normalizeMessageMedia(data)
}

function mediaContentApiPath(downloadUrl: string): string {
  const value = downloadUrl.trim()
  if (!value) return value
  try {
    const parsed = new URL(value, window.location.origin)
    const path = `${parsed.pathname}${parsed.search}`
    return path.startsWith('/api/') ? path.slice(4) : path
  } catch {
    return value.startsWith('/api/') ? value.slice(4) : value
  }
}

export async function fetchMessageMediaContent(downloadUrl: string): Promise<Blob> {
  if (isMockEnabled()) {
    await mockDelay()
    return new Blob(['mock media content'], { type: 'application/octet-stream' })
  }
  const { data } = await client.get<Blob>(mediaContentApiPath(downloadUrl), { responseType: 'blob' })
  return data
}

function deriveManualContentType(content: string, files?: File[]): string {
  if (content.trim()) return 'text'
  if (!files || files.length === 0) return 'text'
  return files.every((file) => file.type.startsWith('image/')) ? 'image' : 'file'
}

export interface ManualEntryPayload {
  room_id: string
  room_name?: string
  sender_name: string
  send_time: string
  content: string
  content_type?: string
  files?: File[]
}

export interface ManualEntryResult {
  message_id: string
  room_id: string
  normalized: boolean
  binding_status: string
  duplicate: boolean
  status: string
}

export async function fetchManualRoomOptions(): Promise<ManualRoomOption[]> {
  if (isMockEnabled()) {
    await mockDelay()
    return [
      {
        room_id: 'room-hospital-support',
        room_name: '某医院售后群',
        group_source: 'wechat',
        customer_name: '某医院',
        project_name: 'HIS 项目',
      },
      {
        room_id: 'room-project-tech',
        room_name: '某项目技术群',
        group_source: 'manual',
        customer_name: '某客户',
        project_name: 'Portal 项目',
      },
    ]
  }
  const { data } = await client.get<{ items: ManualRoomOption[] }>('/messages/manual-room-options')
  return Array.isArray(data.items) ? data.items : []
}

export async function manualEntry(payload: ManualEntryPayload): Promise<ManualEntryResult> {
  const files = payload.files?.filter((file) => file instanceof File) ?? []
  if (isMockEnabled()) {
    await mockDelay()
    const newMsg: MessageItem = {
      message_id: `msg-manual-${Date.now()}`,
      room_id: payload.room_id,
      room_name: payload.room_name || payload.room_id,
      customer_name: '',
      sender_name: payload.sender_name,
      content_preview: payload.content.slice(0, 80),
      content_type: payload.content_type || deriveManualContentType(payload.content, files),
      binding_status: 'unmatched',
      message_status: 'binding_required',
      send_time: payload.send_time,
      ingested_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      operator_actions: makeMockOperatorActions('binding_required'),
    }
    mockMessages.unshift(newMsg)
    mockMessageContent.set(newMsg.message_id, payload.content)
    return {
      message_id: newMsg.message_id,
      room_id: payload.room_id,
      normalized: true,
      binding_status: newMsg.binding_status,
      duplicate: false,
      status: newMsg.message_status,
    }
  }
  if (files.length > 0) {
    const formData = new FormData()
    formData.append('room_id', payload.room_id)
    formData.append('room_name', payload.room_name || '')
    formData.append('sender_name', payload.sender_name)
    formData.append('send_time', payload.send_time)
    formData.append('content', payload.content)
    files.forEach((file) => {
      formData.append('files', file)
    })
    const { data } = await client.post<ManualEntryResult>('/messages/manual-upload', formData)
    return data
  }
  const { data } = await client.post<ManualEntryResult>('/messages/manual', {
    room_id: payload.room_id,
    room_name: payload.room_name,
    sender_name: payload.sender_name,
    send_time: payload.send_time,
    content: payload.content,
    content_type: payload.content_type || 'text',
  })
  return data
}
